import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getTotal, clearCart } = useCart();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryType, setDeliveryType] = useState('retirada');
  const [notes, setNotes] = useState('');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const hasHiddenPriceItems = cart.some((item) => item.omit_price);

  const getItemPrice = (item) => {
    if (item.omit_price) return null;
    if (item.promo_price != null && item.price != null && item.promo_price < item.price) {
      return item.promo_price;
    }
    return item.price != null ? item.price : null;
  };

  const getVisibleTotal = () => {
    return cart.reduce((total, item) => {
      const price = getItemPrice(item);
      if (price == null) return total;
      return total + price * item.quantity;
    }, 0);
  };

  const formatWhatsAppMessage = () => {
    const now = new Date();
    const date = now.toLocaleDateString('pt-BR');
    const time = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    let message = `*Novo Pedido - ${settings?.store_name || 'Central de Ferragens'}*\n\n`;
    message += `📅 Data/Hora: ${date} às ${time}\n`;
    message += `👤 Nome: ${customerName}\n`;
    message += `📞 Telefone: ${customerPhone}\n`;
    message += `🚚 Tipo: ${deliveryType === 'retirada' ? 'Retirada na Loja' : 'Entrega'}\n\n`;

    message += `*Itens do Pedido:*\n`;
    message += `${'='.repeat(35)}\n`;

    cart.forEach((item, index) => {
      const price = getItemPrice(item);
      const subtotal = price != null ? price * item.quantity : null;

      message += `${index + 1}. *${item.name}*\n`;

      if (item.reference) {
        message += `   Ref: ${item.reference}\n`;
      }

      if (price == null) {
        message += `   Qtd: ${item.quantity}x | Valor: Adicione ao carrinho e consulte o preço\n\n`;
      } else {
        message += `   Qtd: ${item.quantity}x | Valor: R$ ${price.toFixed(2)}\n`;
        message += `   Subtotal: R$ ${subtotal.toFixed(2)}\n\n`;
      }
    });

    message += `${'='.repeat(35)}\n`;

    if (hasHiddenPriceItems) {
      message += `*Total parcial dos itens com preço visível: R$ ${getVisibleTotal().toFixed(2)}*\n`;
      message += `*Há itens no pedido com preço a consultar.*\n`;
    } else {
      message += `*TOTAL: R$ ${getVisibleTotal().toFixed(2)}*\n`;
    }

    if (notes) {
      message += `\n📝 Observações: ${notes}\n`;
    }

    if (settings?.footer_message) {
      message += `\n${settings.footer_message}`;
    }

    return encodeURIComponent(message);
  };

  const handleSendWhatsApp = () => {
    if (!customerName.trim()) {
      toast.error('Por favor, informe seu nome');
      return;
    }
    if (!customerPhone.trim()) {
      toast.error('Por favor, informe seu telefone');
      return;
    }

    const whatsappNumber = settings?.whatsapp_number || '555432827737';
    const message = formatWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    window.open(whatsappUrl, '_blank');

    toast.success('Redirecionando para o WhatsApp...', {
      duration: 2000,
    });

    // Optional: Clear cart after sending
    // clearCart();
  };

  if (cart.length === 0) {
    return (
      <div className="App">
        <Header />
        <main className="min-h-[60vh] flex items-center justify-center py-16">
          <div className="text-center" data-testid="empty-cart">
            <ShoppingBag size={80} className="mx-auto mb-6 text-slate-300" />
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#273e82' }}>
              Seu carrinho está vazio
            </h2>
            <p className="text-slate-600 mb-8">
              Adicione produtos ao seu carrinho para fazer um pedido
            </p>
            <button
              onClick={() => navigate('/')}
              className="primary-button"
              data-testid="continue-shopping-button"
            >
              Continuar Comprando
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />

      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
            Carrinho de Compras
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {cart.map((item) => {
                  const price = getItemPrice(item);
                  const subtotal = price != null ? price * item.quantity : null;
                  const mainImage = item.images && item.images.length > 0
                    ? item.images[0].image_url
                    : 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=200';

                  return (
                    <div
                      key={item.id}
                      className="flex gap-4 p-4 border-b border-slate-200 last:border-b-0"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <img
                        src={mainImage}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg bg-slate-100"
                        data-testid="cart-item-image"
                      />

                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-2" style={{ color: '#273e82' }} data-testid="cart-item-name">
                          {item.name}
                        </h3>

                        {item.reference && (
                          <p className="text-sm text-slate-500 mb-2">
                            Ref: {item.reference}
                          </p>
                        )}

                        <p className="text-slate-600 mb-3" data-testid="cart-item-price">
                          {price == null ? 'Adicione ao carrinho e consulte o preço' : `R$ ${price.toFixed(2)} / unidade`}
                        </p>

                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 rounded border-2 border-slate-300 hover:border-[#273e82] font-bold transition-colors"
                              data-testid={`decrease-quantity-${item.id}`}
                            >
                              <Minus size={16} className="mx-auto" />
                            </button>
                            <span className="text-lg font-bold min-w-8 text-center" data-testid={`quantity-${item.id}`}>
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 rounded border-2 border-slate-300 hover:border-[#273e82] font-bold transition-colors"
                              data-testid={`increase-quantity-${item.id}`}
                            >
                              <Plus size={16} className="mx-auto" />
                            </button>
                          </div>

                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-600 p-2 transition-colors"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xl font-bold" style={{ color: '#273e82' }} data-testid={`subtotal-${item.id}`}>
                          {subtotal == null ? 'A consultar' : `R$ ${subtotal.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-6" style={{ color: '#273e82', fontFamily: 'Manrope' }}>
                  Finalizar Pedido
                </h2>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Seu nome"
                      className="input-field"
                      data-testid="customer-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                      Telefone/WhatsApp *
                    </label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="(00) 00000-0000"
                      className="input-field"
                      data-testid="customer-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                      Tipo de Recebimento
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="retirada"
                          checked={deliveryType === 'retirada'}
                          onChange={(e) => setDeliveryType(e.target.value)}
                          className="w-4 h-4"
                          data-testid="delivery-type-pickup"
                        />
                        <span>Retirada na Loja</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="entrega"
                          checked={deliveryType === 'entrega'}
                          onChange={(e) => setDeliveryType(e.target.value)}
                          className="w-4 h-4"
                          data-testid="delivery-type-delivery"
                        />
                        <span>Entrega</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#273e82' }}>
                      Observações
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Ex: Se tiver similar pode trocar, entregar no endereço X..."
                      rows={4}
                      className="input-field resize-none"
                      data-testid="notes-input"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">Itens ({cart.length})</span>
                    <span className="font-semibold" style={{ color: '#273e82' }}>
                      {hasHiddenPriceItems ? 'A consultar' : `R$ ${getVisibleTotal().toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span style={{ color: '#273e82' }}>Total</span>
                    <span style={{ color: '#273e82' }} data-testid="cart-total">
                      {hasHiddenPriceItems ? 'A consultar' : `R$ ${getVisibleTotal().toFixed(2)}`}
                    </span>
                  </div>
                  {hasHiddenPriceItems && (
                    <p className="text-xs text-slate-500 mt-2">
                      Existem itens no carrinho com preço sob consulta.
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSendWhatsApp}
                  className="w-full secondary-button text-lg"
                  data-testid="send-whatsapp-button"
                >
                  📱 Enviar Pedido no WhatsApp
                </button>

                <p className="text-xs text-slate-500 mt-4 text-center">
                  Ao clicar, você será redirecionado para o WhatsApp com seu pedido pronto
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;